import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Video, Download, Flower2, ShoppingBag, LogOut, Radio, Plus } from "lucide-react";
import { api, errMsg, eur, fmtDate, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND, LOGO } from "@/lib/brand";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PayButtons } from "@/components/PayButtons";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, Empty, Field } from "@/components/common";
import { downloadAuth } from "@/components/shared";

const Stream = ({ me }) => (
  <Card className="p-6" data-testid="cliente-stream">
    <p className="font-display text-2xl font-semibold text-[#0D3B52] flex items-center gap-2"><Radio className="h-5 w-5 text-[#B8860B]" />Cremazione teleassistita</p>
    {me.stream_url ? (
      <>
        <p className="text-sm text-[#5C6B73] mt-2">{me.stream_note || "Il collegamento sarà attivo all'orario concordato con l'agenzia."}</p>
        {/youtube|youtu\.be/.test(me.stream_url) ? (
          <iframe title="stream" className="w-full aspect-video rounded-xl mt-4" src={me.stream_url.replace("watch?v=", "embed/").replace("youtu.be/", "www.youtube.com/embed/")} allow="autoplay; fullscreen" />
        ) : (
          <a href={me.stream_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0D3B52] text-white px-6 py-3 font-medium" data-testid="cliente-stream-link">Apri la diretta</a>
        )}
      </>
    ) : <p className="text-sm text-[#5C6B73] mt-2">Nessuna diretta programmata al momento. L'agenzia attiverà il collegamento nel giorno della cremazione.</p>}
  </Card>
);

const Files = ({ files }) => (
  <Card className="p-6" data-testid="cliente-files">
    <p className="font-display text-2xl font-semibold text-[#0D3B52] flex items-center gap-2"><Video className="h-5 w-5 text-[#B8860B]" />Video e documenti</p>
    {files.length === 0 ? <p className="text-sm text-[#5C6B73] mt-2">Nessun file ancora disponibile.</p> : files.map((f) => (
      <div key={f.id} className="flex items-center justify-between border-t border-[#E2DDD5] py-3 text-sm">
        <span>{f.title} <span className="text-xs text-[#5C6B73]">· {fmtDate(f.created_at)}</span></span>
        <Button size="sm" variant="outline" onClick={() => downloadAuth(`/cliente/files/${f.id}/download`, f.original_filename)} data-testid={`cliente-file-${f.id}`}><Download className="h-4 w-4 mr-1" />Scarica</Button>
      </div>
    ))}
  </Card>
);

const Memorial = ({ user }) => {
  const [mine, setMine] = useState([]);
  const [f, setF] = useState({ pet_name: user.pet_name || "", dedica: "", anni: "" });
  const [file, setFile] = useState(null);
  const load = useCallback(() => api.get("/cliente/cimitero").then((r) => setMine(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!f.pet_name.trim() || !file) return toast.error("Inserisci il nome e una foto");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    try { await api.post("/cliente/cimitero", fd); toast.success("Dedica pubblicata nel Cimitero virtuale"); setFile(null); setF({ ...f, dedica: "" }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-6" data-testid="cliente-memorial">
      <p className="font-display text-2xl font-semibold text-[#0D3B52] flex items-center gap-2"><Flower2 className="h-5 w-5 text-[#B8860B]" />Cimitero virtuale</p>
      <p className="text-sm text-[#5C6B73] mt-1">Carica una foto del tuo piccolo e lascia una dedica: sarà visibile a tutti nel <Link to="/cimitero" className="underline">Cimitero virtuale</Link>.</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <Field label="Nome"><Input value={f.pet_name} onChange={(e) => setF({ ...f, pet_name: e.target.value })} className="bg-white" data-testid="memorial-pet" /></Field>
        <Field label="Anni insieme / date"><Input value={f.anni} onChange={(e) => setF({ ...f, anni: e.target.value })} placeholder="es. 2010 – 2026" className="bg-white" /></Field>
        <Field label="Dedica" className="sm:col-span-2"><Textarea rows={3} value={f.dedica} onChange={(e) => setF({ ...f, dedica: e.target.value })} className="bg-white" data-testid="memorial-dedica" /></Field>
        <Field label="Foto" className="sm:col-span-2"><input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="memorial-foto" /></Field>
      </div>
      <Button onClick={send} className="rounded-full bg-[#0D3B52] hover:bg-[#082535] mt-4" data-testid="memorial-send"><Plus className="h-4 w-4 mr-1" />Pubblica dedica</Button>
      {mine.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">{mine.map((m) => <div key={m.id} className="rounded-xl overflow-hidden border border-[#E2DDD5]"><img src={`${BACKEND_URL}/api/public/cimitero/${m.id}/foto`} alt={m.pet_name} className="h-32 w-full object-cover" /><p className="p-2 text-sm font-semibold text-[#0D3B52]">{m.pet_name}</p></div>)}</div>}
    </Card>
  );
};

const Catalogo = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/catalogo").then((r) => setItems(r.data)); }, []);
  return items.length === 0 ? <Empty>Il catalogo sarà presto disponibile.</Empty> : (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cliente-catalogo">
      {items.map((c) => (
        <Card key={c.id} className="overflow-hidden">
          {c.storage_path && <img src={`${BACKEND_URL}/api/catalogo/${c.id}/foto`} alt={c.nome} className="h-48 w-full object-cover" />}
          <div className="p-4">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#997316] font-semibold">{c.categoria}</p>
            <div className="flex justify-between items-baseline"><p className="font-display text-xl font-semibold text-[#0D3B52]">{c.nome}</p>{c.prezzo != null && <p className="font-semibold">{eur(c.prezzo)}</p>}</div>
            <p className="text-sm text-[#5C6B73] mt-1">{c.descrizione}</p>
            <a href={`https://wa.me/${BRAND.wa}?text=${encodeURIComponent(`Buongiorno, vorrei informazioni su: ${c.nome}`)}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-[#1DA851] font-medium"><ShoppingBag className="h-4 w-4" />Richiedi su WhatsApp</a>
          </div>
        </Card>
      ))}
    </div>
  );
};

const Pagamento = ({ pg, email }) => {
  if (!pg) return <Card className="p-6 text-sm text-[#5C6B73]" data-testid="cliente-pagamento-empty">Nessun servizio collegato al suo account. Per informazioni sui pagamenti ci contatti al 389 244 6910.</Card>;
  return (
    <Card className="p-6 space-y-4" data-testid="cliente-pagamento">
      <div>
        <p className="text-[11px] uppercase tracking-[0.14em] text-[#997316] font-semibold">Servizio per {pg.animale}</p>
        <p className="font-display text-3xl text-[#0D3B52] mt-1">{pg.residuo > 0 ? `Saldo da pagare: ${eur(pg.residuo)}` : "Servizio interamente pagato"}</p>
        <p className="text-sm text-[#5C6B73] mt-1">Totale {eur(pg.totale)} · acconto {eur(pg.acconto)}</p>
      </div>
      {pg.residuo > 0 && <PayButtons praticaId={pg.pratica_id} amount={pg.residuo} email={email && !email.endsWith("@cliente.local") ? email : undefined} testid="cliente-pay" />}
      <p className="text-xs text-[#5C6B73]">Pagamento sicuro tramite Stripe. Con Klarna può dividere l'importo in 3 rate: viene applicata una maggiorazione del 6% per coprire le commissioni del servizio.</p>
    </Card>
  );
};

export default function ClienteDashboard() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/cliente/me").then((r) => setMe(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="cliente-dashboard">
      <header className="bg-white/90 backdrop-blur border-b border-[#E2DDD5] h-16 px-4 sm:px-8 flex items-center justify-between sticky top-0 z-20">
        <img src={LOGO} alt={BRAND.name} className="h-9 w-auto" />
        <div className="flex items-center gap-3 text-sm"><span className="hidden sm:inline text-[#5C6B73]">{user.name}</span><button onClick={logout} className="inline-flex items-center gap-1 text-[#0D3B52]" data-testid="cliente-logout"><LogOut className="h-4 w-4" />Esci</button></div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8 space-y-6">
        <div><p className="text-[11px] uppercase tracking-[0.2em] text-[#997316] font-semibold">Area riservata</p><h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#0D3B52]">Ciao {user.name}{user.pet_name ? `, in ricordo di ${user.pet_name}` : ""}</h1></div>
        <Tabs defaultValue="pagamento">
          <TabsList className="bg-white border border-[#E2DDD5] h-auto p-1 flex-wrap">
            {[["pagamento", "Pagamento"], ["stream", "Teleassistita"], ["files", "Video ricordo"], ["memorial", "Cimitero virtuale"], ["catalogo", "Catalogo"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`cliente-tab-${v}`} className="data-[state=active]:bg-[#0D3B52] data-[state=active]:text-white">{l}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="pagamento" className="mt-6">{me && <Pagamento pg={me.pagamento} email={user?.email} />}</TabsContent>
          <TabsContent value="stream" className="mt-6">{me && <Stream me={me} />}</TabsContent>
          <TabsContent value="files" className="mt-6">{me && <Files files={me.files} />}</TabsContent>
          <TabsContent value="memorial" className="mt-6"><Memorial user={user} /></TabsContent>
          <TabsContent value="catalogo" className="mt-6"><Catalogo /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
